import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Switch, TextField, Button, Snackbar, Alert,
  FormControlLabel, Paper, Divider,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import { getSettings, saveSettings } from '../api/whatsapp';

const ChatSettings = ({ account, onUpdated }) => {
  const [aiEnabled, setAiEnabled] = useState(false);
  const [greetingMessage, setGreetingMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getSettings();
        const s = data.account?.settings || {};
        setAiEnabled(s.aiEnabled || false);
        setGreetingMessage(s.greetingMessage || '');
      } catch {
        setSnack({ open: true, message: 'Failed to load settings', severity: 'error' });
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSettings({
        aiEnabled,
        greetingMessage: greetingMessage.trim(),
        autoReplyEnabled: aiEnabled,
        autoReplyMessage: '',
      });
      setSnack({ open: true, message: 'Settings saved!', severity: 'success' });
      if (onUpdated) onUpdated();
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.message || 'Failed to save', severity: 'error' });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="#64748b">Loading settings...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* AI Auto-Reply */}
      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <SmartToyIcon sx={{ color: aiEnabled ? '#6366f1' : '#94a3b8', fontSize: 22 }} />
          <Typography fontWeight={700} fontSize={16} color="#1e293b">AI Auto-Reply</Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={aiEnabled}
              onChange={(e) => setAiEnabled(e.target.checked)}
              color="primary"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#6366f1' },
              }}
            />
          }
          label={
            <Box>
              <Typography fontWeight={600} fontSize={14} color="#1e293b">
                {aiEnabled ? 'AI Auto-Reply is ON' : 'AI Auto-Reply is OFF'}
              </Typography>
              <Typography fontSize={12} color="#64748b">
                {aiEnabled
                  ? 'When a user sends a message, AI will match keywords and send auto-replies automatically.'
                  : 'All messages go directly to live agents. No auto-replies are sent.'}
              </Typography>
            </Box>
          }
          sx={{ m: 0, alignItems: 'flex-start' }}
        />
      </Paper>

      {/* Greeting Message */}
      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <WavingHandIcon sx={{ color: greetingMessage ? '#f59e0b' : '#94a3b8', fontSize: 22 }} />
          <Typography fontWeight={700} fontSize={16} color="#1e293b">Greeting Message</Typography>
        </Box>
        <Typography fontSize={13} color="#64748b" mb={2}>
          Sent once to each user when they send their first message. Leave empty to disable.
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          value={greetingMessage}
          onChange={(e) => setGreetingMessage(e.target.value)}
          placeholder="e.g. Welcome! How can I help you today?"
          inputProps={{ maxLength: 500 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f8fafc',
              borderRadius: 2,
              '& fieldset': { borderColor: '#e2e8f0' },
              '&:hover fieldset': { borderColor: '#cbd5e1' },
              '&.Mui-focused fieldset': { borderColor: '#6366f1' },
            },
          }}
        />
        <Typography fontSize={11} color="#94a3b8" mt={0.5}>
          {greetingMessage.length}/500 characters
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            bgcolor: '#075e54',
            '&:hover': { bgcolor: '#064e46' },
            textTransform: 'none',
            borderRadius: 2,
            px: 4,
            fontWeight: 600,
          }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatSettings;
